/**
 * NeoCore · Conciliación bancaria
 *
 * Flujo:
 *   1. Seleccionar cuenta bancaria propia (Interbanco o BAC)
 *   2. Subir extracto (XLSX / CSV) → parser normaliza + import RPC
 *   3. Ver movimientos importados con estado de conciliación
 *   4. Match manual contra boleta_bancaria si el auto falló
 *   5. Marcar como IGNORADO (comisiones bancarias, etc.)
 *
 * Task #145.
 */

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import {
  UploadSimple, Bank, CheckCircle, Warning, XCircle, ClockCounterClockwise,
  Link as LinkIcon, ArrowsClockwise,
} from '@phosphor-icons/react'
import { parseExtractoBancario, type BancoOrigen, type MovimientoParseado } from '@/lib/import/parseExtractoBancario'
import { haceDiasGT, hoyGT } from '../lib/fechas'

type Cuenta = { id: string; banco: string; alias: string | null; numero_cuenta: string; moneda: string }

type ConcRow = {
  movimiento_id: string
  fecha: string
  tipo: 'CREDITO' | 'DEBITO'
  monto: number
  descripcion: string | null
  referencia: string | null
  estado: 'PENDIENTE' | 'AUTOMATICA' | 'MANUAL' | 'IGNORADO'
  boleta_id: string | null
  boleta_numero: string | null
  boleta_fecha: string | null
  boleta_monto_total: number | null
  score: number | null
  conciliado_at: string | null
  notas: string | null
}

type BoletaOpt = {
  id: string
  fecha_boleta: string
  numero_boleta: string
  monto_total: number
  monto_aplicado: number
  estado: string
}

const fmt = (n: number) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const hoy = () => hoyGT()
const hace30 = () => haceDiasGT(30)

export function NeoCoreConciliacionBancariaPage() {
  const [cuentas, setCuentas] = useState<Cuenta[]>([])
  const [cuentaId, setCuentaId] = useState('')
  const [desde, setDesde] = useState(hace30())
  const [hasta, setHasta] = useState(hoy())
  const [filtroEstado, setFiltroEstado] = useState<string>('')
  const [rows, setRows] = useState<ConcRow[]>([])
  const [loading, setLoading] = useState(false)
  const [importResult, setImportResult] = useState<any>(null)
  const [importErr, setImportErr] = useState<string | null>(null)
  const [matchModal, setMatchModal] = useState<ConcRow | null>(null)

  useEffect(() => {
    supabase.from('cuenta_bancaria_propia').select('id, banco, alias, numero_cuenta, moneda')
      .eq('activa', true).order('banco')
      .then(({ data }) => {
        const list = (data ?? []) as Cuenta[]
        setCuentas(list)
        if (list.length > 0) setCuentaId(list[0].id)
      })
  }, [])

  const cargar = async () => {
    if (!cuentaId) return
    setLoading(true)
    const { data, error } = await supabase.rpc('listar_conciliacion', {
      p_cuenta_bancaria_propia_id: cuentaId,
      p_fecha_desde: desde,
      p_fecha_hasta: hasta,
      p_estado: filtroEstado || null,
    })
    setLoading(false)
    if (error) { setImportErr(error.message); return }
    setRows((data ?? []) as ConcRow[])
  }

  useEffect(() => { cargar() }, [cuentaId, desde, hasta, filtroEstado])

  const detectarBanco = (nombre: string): BancoOrigen => {
    const n = nombre.toLowerCase()
    if (n.includes('inter') || n.includes('bi')) return 'INTERBANCO'
    if (n.includes('bac')) return 'BAC'
    return 'OTRO'
  }

  const onFile = async (file: File) => {
    setImportErr(null); setImportResult(null)
    const cuenta = cuentas.find(c => c.id === cuentaId)
    if (!cuenta) { setImportErr('Selecciona una cuenta primero'); return }
    const banco = detectarBanco(cuenta.banco)

    try {
      const buf = await file.arrayBuffer()
      const parsed = await parseExtractoBancario(buf, banco)

      if (parsed.movimientos.length === 0) {
        setImportErr(`No se pudo leer el archivo. Advertencias: ${parsed.advertencias.join('; ')}`)
        return
      }

      const { data, error } = await supabase.rpc('importar_lote_bancario', {
        p_cuenta_bancaria_propia_id: cuentaId,
        p_banco: banco,
        p_filename: file.name,
        p_movimientos: parsed.movimientos as unknown as MovimientoParseado[],
      })
      if (error) { setImportErr(error.message); return }
      setImportResult({ ...(data as any), descartados: parsed.descartados, advertencias: parsed.advertencias })
      await cargar()
    } catch (e: any) {
      setImportErr(e.message ?? 'Error inesperado')
    }
  }

  const contadores = rows.reduce((acc, r) => {
    acc[r.estado] = (acc[r.estado] ?? 0) + 1
    return acc
  }, {} as Record<string, number>)

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 500 }}>Conciliación bancaria</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--muted-foreground)', fontSize: 13 }}>
          Subí el extracto y NeoCore cruza cada movimiento con las boletas registradas.
          Interbanco y BAC autodetectados. Los que no matchean quedan pendientes de revisión manual.
        </p>
      </div>

      {/* Barra de acción */}
      <div style={{
        display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 16,
        padding: 12, background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8,
      }}>
        <select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)} style={inp}>
          <option value="">Seleccionar cuenta…</option>
          {cuentas.map(c => (
            <option key={c.id} value={c.id}>
              {c.banco} · {c.numero_cuenta} · {c.moneda} {c.alias ? `(${c.alias})` : ''}
            </option>
          ))}
        </select>
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={inp} />
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={inp} />
        <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} style={inp}>
          <option value="">Todos los estados</option>
          <option value="PENDIENTE">Pendiente</option>
          <option value="AUTOMATICA">Automática</option>
          <option value="MANUAL">Manual</option>
          <option value="IGNORADO">Ignorado</option>
        </select>
        <button onClick={cargar} disabled={loading} style={btnGhost}>
          <ArrowsClockwise size={13} /> Refrescar
        </button>
      </div>

      {/* Upload */}
      <label style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: 14,
        border: '2px dashed var(--border)', borderRadius: 8, cursor: 'pointer',
        background: 'var(--surface, #fff)',
      }}>
        <UploadSimple size={20} color="var(--brand-600, #FF5B04)" />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Subir extracto (XLSX / CSV)</div>
          <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
            Interbanco y BAC autodetectados. Otros bancos usan mapeo genérico.
          </div>
        </div>
        <input type="file" accept=".xlsx,.xls,.csv,.tsv" hidden onChange={(e) => {
          const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''
        }} />
      </label>

      {/* Resultado del import */}
      {importErr && (
        <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {importErr}
        </div>
      )}
      {importResult && (
        <div style={{ padding: 12, background: '#dcfce7', color: '#166534', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          Importados <b>{importResult.insertados}</b> movimientos · duplicados descartados <b>{importResult.duplicados}</b> ·
          auto-conciliados <b>{importResult.auto_match}</b> ·
          créditos <b>Q {fmt(importResult.total_creditos)}</b> · débitos <b>Q {fmt(importResult.total_debitos)}</b>
          {importResult.descartados > 0 && <> · filas descartadas al parsear <b>{importResult.descartados}</b></>}
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <Kpi label="Pendientes" value={contadores.PENDIENTE ?? 0} icon={ClockCounterClockwise} color="#B45309" />
        <Kpi label="Auto-conciliados" value={contadores.AUTOMATICA ?? 0} icon={CheckCircle} color="#047857" />
        <Kpi label="Manuales" value={contadores.MANUAL ?? 0} icon={LinkIcon} color="#2563EB" />
        <Kpi label="Ignorados" value={contadores.IGNORADO ?? 0} icon={XCircle} color="#71717A" />
      </div>

      {/* Tabla */}
      <div style={{ background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--n-50, #f4f4f5)', borderBottom: '1px solid var(--border)' }}>
            <tr>
              <th style={th}>Fecha</th>
              <th style={th}>Tipo</th>
              <th style={{ ...th, textAlign: 'right' }}>Monto</th>
              <th style={th}>Descripción</th>
              <th style={th}>Referencia</th>
              <th style={th}>Estado</th>
              <th style={th}>Boleta matcheada</th>
              <th style={th}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)' }}>
                Sin movimientos en el rango
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.movimiento_id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={td}>{r.fecha}</td>
                <td style={{ ...td, color: r.tipo === 'CREDITO' ? '#047857' : '#B91C1C', fontWeight: 500 }}>
                  {r.tipo === 'CREDITO' ? '↓ Crédito' : '↑ Débito'}
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                  Q {fmt(r.monto)}
                </td>
                <td style={{ ...td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.descripcion}
                </td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.referencia}</td>
                <td style={td}><EstadoPill estado={r.estado} score={r.score} /></td>
                <td style={{ ...td, fontSize: 11 }}>
                  {r.boleta_id ? (
                    <div>
                      <div><b>#{r.boleta_numero}</b></div>
                      <div style={{ color: 'var(--muted-foreground)' }}>{r.boleta_fecha} · Q {fmt(r.boleta_monto_total ?? 0)}</div>
                    </div>
                  ) : <span style={{ color: 'var(--muted-foreground)' }}>—</span>}
                </td>
                <td style={td}>
                  {r.estado === 'PENDIENTE' && r.tipo === 'CREDITO' && (
                    <button onClick={() => setMatchModal(r)} style={btnMini}>
                      Buscar boleta
                    </button>
                  )}
                  {r.estado === 'PENDIENTE' && (
                    <button onClick={async () => {
                      const motivo = prompt('Motivo (ej: comisión bancaria):')
                      if (!motivo) return
                      await supabase.rpc('conciliar_ignorar', { p_movimiento_id: r.movimiento_id, p_motivo: motivo })
                      await cargar()
                    }} style={{ ...btnMini, marginLeft: 4 }}>
                      Ignorar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {matchModal && (
        <MatchModal
          movimiento={matchModal}
          cuentaId={cuentaId}
          onClose={() => setMatchModal(null)}
          onDone={async () => { setMatchModal(null); await cargar() }}
        />
      )}
    </div>
  )
}

function EstadoPill({ estado, score }: { estado: string; score: number | null }) {
  const map: Record<string, { bg: string; fg: string; label: string; Icon: any }> = {
    PENDIENTE:  { bg: '#fef3c7', fg: '#B45309', label: 'Pendiente', Icon: Warning },
    AUTOMATICA: { bg: '#dcfce7', fg: '#047857', label: 'Auto', Icon: CheckCircle },
    MANUAL:     { bg: '#dbeafe', fg: '#2563EB', label: 'Manual', Icon: LinkIcon },
    IGNORADO:   { bg: '#f4f4f5', fg: '#71717A', label: 'Ignorado', Icon: XCircle },
  }
  const s = map[estado] ?? map.PENDIENTE
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px',
      background: s.bg, color: s.fg, borderRadius: 10, fontSize: 10, fontWeight: 500, textTransform: 'uppercase',
    }}>
      <s.Icon size={10} /> {s.label}{score ? ` ${Math.round(score * 100)}%` : ''}
    </span>
  )
}

function Kpi({ label, value, icon: Icon, color }: { label: string; value: number; icon: any; color: string }) {
  return (
    <div style={{ padding: 14, background: 'var(--surface, #fff)', border: '1px solid var(--border)',
      borderRadius: 10, borderLeft: `3px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--muted-foreground)',
        textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
        <Icon size={12} /> {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 500, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  )
}

// ── Modal de match manual ─────────────────────────────────────────────

function MatchModal({ movimiento, cuentaId, onClose, onDone }: {
  movimiento: ConcRow; cuentaId: string; onClose: () => void; onDone: () => void
}) {
  const [boletas, setBoletas] = useState<BoletaOpt[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    // Buscar boletas de esta cuenta con saldo disponible cerca de la fecha ±15 días
    const desde = new Date(movimiento.fecha); desde.setDate(desde.getDate() - 15)
    const hasta = new Date(movimiento.fecha); hasta.setDate(hasta.getDate() + 15)
    supabase.from('boleta_bancaria')
      .select('id, fecha_boleta, numero_boleta, monto_total, monto_aplicado, estado')
      .eq('cuenta_bancaria_propia_id', cuentaId)
      .gte('fecha_boleta', desde.toISOString().slice(0, 10))
      .lte('fecha_boleta', hasta.toISOString().slice(0, 10))
      .order('fecha_boleta', { ascending: false })
      .then(({ data }) => {
        setBoletas((data ?? []) as BoletaOpt[])
        setLoading(false)
      })
  }, [movimiento, cuentaId])

  const match = async (boletaId: string) => {
    setSaving(true)
    await supabase.rpc('conciliar_manual_boleta', {
      p_movimiento_id: movimiento.movimiento_id,
      p_boleta_id: boletaId,
      p_notas: `Match manual ${hoyGT()}`,
    })
    setSaving(false)
    onDone()
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--surface, #fff)', borderRadius: 12, padding: 20, maxWidth: 700, width: '90%', maxHeight: '80vh', overflow: 'auto',
      }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Buscar boleta para conciliar</h3>
        <div style={{ margin: '8px 0 16px', padding: 10, background: '#f4f4f5', borderRadius: 6, fontSize: 12 }}>
          Movimiento: <b>{movimiento.fecha}</b> · Q {fmt(movimiento.monto)} · {movimiento.descripcion ?? '—'}
        </div>
        {loading ? <div>Cargando boletas…</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={th}>Fecha</th><th style={th}>Número</th>
                <th style={{ ...th, textAlign: 'right' }}>Total</th>
                <th style={{ ...th, textAlign: 'right' }}>Aplicado</th>
                <th style={th}>Estado</th><th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {boletas.length === 0 && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: 'var(--muted-foreground)' }}>Sin boletas cerca de la fecha</td></tr>}
              {boletas.map(b => {
                const coincideMonto = Number(b.monto_total) === Number(movimiento.monto)
                return (
                  <tr key={b.id} style={{ borderBottom: '1px solid var(--border)', background: coincideMonto ? '#dcfce7' : undefined }}>
                    <td style={td}>{b.fecha_boleta}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{b.numero_boleta}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: coincideMonto ? 500 : 400 }}>Q {fmt(b.monto_total)}</td>
                    <td style={{ ...td, textAlign: 'right', color: 'var(--muted-foreground)' }}>Q {fmt(b.monto_aplicado)}</td>
                    <td style={{ ...td, fontSize: 10, textTransform: 'uppercase' }}>{b.estado}</td>
                    <td style={td}>
                      <button onClick={() => match(b.id)} disabled={saving} style={btnMini}>Matchear</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button onClick={onClose} style={btnGhost}>Cerrar</button>
        </div>
      </div>
    </div>
  )
}

// ── styles ────────────────────────────────────────────────────────────

const inp: React.CSSProperties = {
  padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6,
  fontSize: 12, background: 'var(--surface, #fff)', fontFamily: 'inherit',
}
const btnGhost: React.CSSProperties = {
  padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 6,
  background: 'var(--surface, #fff)', fontSize: 12, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
}
const btnMini: React.CSSProperties = {
  padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4,
  background: 'var(--surface, #fff)', fontSize: 11, cursor: 'pointer',
}
const th: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'left', fontSize: 11,
  color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500,
}
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' }

// Silencia warning de import no usado
void Bank
